// Magic UI — Border Beam (https://magicui.design/docs/components/border-beam)
// Adapted: brand colors by default. Used to highlight the order currently in progress.
import { motion, type MotionStyle, type Transition } from "motion/react";
import { cn } from "../cn";

interface BorderBeamProps {
  size?: number;
  duration?: number;
  delay?: number;
  colorFrom?: string;
  colorTo?: string;
  transition?: Transition;
  className?: string;
  style?: React.CSSProperties;
  reverse?: boolean;
  initialOffset?: number;
  borderWidth?: number;
}

export const BorderBeam = ({
  className,
  size = 60,
  delay = 0,
  duration = 7,
  colorFrom = "#12b284",
  colorTo = "#f7a524",
  transition,
  style,
  reverse = false,
  initialOffset = 0,
  borderWidth = 2,
}: BorderBeamProps) => (
  <div
    aria-hidden="true"
    className="pointer-events-none absolute inset-0 rounded-[inherit] border-(length:--border-beam-width) border-transparent mask-[linear-gradient(transparent,transparent),linear-gradient(#000,#000)] mask-intersect [mask-clip:padding-box,border-box]"
    style={{ "--border-beam-width": `${borderWidth}px` } as React.CSSProperties}
  >
    <motion.div
      className={cn(
        "absolute aspect-square",
        "bg-linear-to-l from-(--color-from) via-(--color-to) to-transparent",
        className,
      )}
      style={
        {
          width: size,
          offsetPath: `rect(0 auto auto 0 round ${size}px)`,
          "--color-from": colorFrom,
          "--color-to": colorTo,
          ...style,
        } as MotionStyle
      }
      initial={{ offsetDistance: `${initialOffset}%` }}
      animate={{
        offsetDistance: reverse
          ? [`${100 - initialOffset}%`, `${-initialOffset}%`]
          : [`${initialOffset}%`, `${100 + initialOffset}%`],
      }}
      transition={{ repeat: Infinity, ease: "linear", duration, delay: -delay, ...transition }}
    />
  </div>
);
